import { JsonToProtobufSampleData } from '../../../../../DummyData/ConvertSampleJson';
import React, { useState } from 'react';

// Define types for our component
interface JsonToProtobufProps {}

// Protocol Buffer syntax options
type ProtoSyntax = 'proto2' | 'proto3';

// Output format options
type OutputFormat = 'single-file' | 'multiple-files';

// Interface for field types mapping
interface TypeMapping {
  [key: string]: string;
}

const JsonToProtobuf: React.FC<JsonToProtobufProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Proto output
  const [protoOutput, setProtoOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the package name
  const [packageName, setPackageName] = useState<string>('my_package');
  // State for the main message name
  const [mainMessageName, setMainMessageName] = useState<string>('Root');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for Proto syntax
  const [syntax, setSyntax] = useState<ProtoSyntax>('proto3');
  // State for output format
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('single-file');
  // State to track if the conversion happened
  const [isProtoGenerated, setIsProtoGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for nested depth limit
  const [nestedDepthLimit, setNestedDepthLimit] = useState<number>(10);
  // State for custom type mappings
  const [customTypes, setCustomTypes] = useState<boolean>(false);
  // State for type mapping string
  const [typeMappingString, setTypeMappingString] = useState<string>(
    JSON.stringify({
      string: 'string',
      number: 'double',
      integer: 'int32',
      boolean: 'bool',
      array: 'repeated'
    }, null, 2)
  );

  // Sample JSON data for example


  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsProtoGenerated(false);
  };

  // Handle package name change
  const handlePackageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPackageName(e.target.value);
  };

  // Handle main message name change
  const handleMainMessageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMainMessageName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(JsonToProtobufSampleData, null, 2));
    setError('');
    setIsProtoGenerated(false);
    setProtoOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setProtoOutput('');
    setError('');
    setPackageName('my_package');
    setMainMessageName('Root');
    setSyntax('proto3');
    setOutputFormat('single-file');
    setIsProtoGenerated(false);
    setNestedDepthLimit(10);
    setCustomTypes(false);
    setTypeMappingString(JSON.stringify({
      string: 'string',
      number: 'double',
      integer: 'int32',
      boolean: 'bool',
      array: 'repeated'
    }, null, 2));
  };

  // Get the JavaScript type of a value
  const getJsType = (value: any): string => {
    if (value === null || value === undefined) {
      return 'null';
    }
    
    if (Array.isArray(value)) {
      return 'array';
    }
    
    if (typeof value === 'number') {
      // Check if it's an integer
      return Number.isInteger(value) ? 'integer' : 'number';
    }
    
    return typeof value;
  };

  // Convert JavaScript type to Protocol Buffer type
  const getProtoType = (jsType: string, typeMappings: TypeMapping): string => {
    return typeMappings[jsType] || 'string'; // Default to string if no mapping found
  };

  // Clean the name to make it suitable for Protocol Buffer identifiers
  const cleanName = (name: string): string => {
    // Replace non-alphanumeric characters with underscores
    let cleaned = name.replace(/[^a-zA-Z0-9_]/g, '_');
    
    // Ensure it doesn't start with a number
    if (/^[0-9]/.test(cleaned)) {
      cleaned = '_' + cleaned;
    }
    
    // Convert to PascalCase for message names
    return cleaned;
  };

  // Convert camelCase or snake_case to PascalCase for message types
  const toPascalCase = (str: string): string => {
    // First convert snake_case to camelCase
    const camelCase = str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    
    // Then convert camelCase to PascalCase
    return camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
  };

  // Convert camelCase or PascalCase to snake_case for field names (proto3 style)
  const toSnakeCase = (str: string): string => {
    // First check if it's already snake_case
    if (str.includes('_')) {
      return str;
    }
    
    // Convert PascalCase or camelCase to snake_case
    return str
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, '');
  };

  // Generate a unique message name based on the context and field
  const generateMessageName = (contextName: string, fieldName: string): string => {
    const fieldNamePascal = toPascalCase(cleanName(fieldName));
    const contextNamePascal = toPascalCase(cleanName(contextName));
    
    // If the field name already contains the context name, just use the field name
    if (fieldNamePascal.includes(contextNamePascal)) {
      return fieldNamePascal;
    }
    
    return `${contextNamePascal}${fieldNamePascal}`;
  };

  // Process a JSON object and generate Protocol Buffer messages
  const processObject = (
    obj: any, 
    messageName: string, 
    depth: number = 0, 
    typeMappings: TypeMapping,
    path: string[] = [],
    processedObjects: Map<string, string> = new Map()
  ): { messages: string[], fields: string[] } => {
    if (depth > nestedDepthLimit) {
      return { messages: [], fields: ['"Max nested depth exceeded"'] };
    }
    
    const messages: string[] = [];
    const fields: string[] = [];
    let fieldCounter = 1;
    
    // Process each property in the object
    for (const [key, value] of Object.entries(obj)) {
      const fieldName = toSnakeCase(key);
      const jsType = getJsType(value);
      
      if (jsType === 'object') {
        // Generate a message name for the nested object
        const nestedMessageName = generateMessageName(messageName, key);
        const pathKey = [...path, key].join('.');
        
        // Check if we've seen this structure before
        if (processedObjects.has(JSON.stringify(value))) {
          const existingType = processedObjects.get(JSON.stringify(value))!;
          fields.push(`${syntax === 'proto3' ? '' : 'optional '}${existingType} ${fieldName} = ${fieldCounter++};`);
        } else {
          // Process the nested object
          const { messages: nestedMessages, fields: nestedFields } = processObject(
            value, 
            nestedMessageName, 
            depth + 1, 
            typeMappings,
            [...path, key],
            processedObjects
          );
          
          // Create the message for the nested object
          const nestedMessage = `message ${nestedMessageName} {\n  ${nestedFields.join('\n  ')}\n}`;
          messages.push(nestedMessage);
          
          // Add a field for the nested object
          fields.push(`${syntax === 'proto3' ? '' : 'optional '}${nestedMessageName} ${fieldName} = ${fieldCounter++};`);
          
          // Remember this structure for reuse
          processedObjects.set(JSON.stringify(value), nestedMessageName);
        }
      } else if (jsType === 'array') {
        const arrayValue = value as any[];
        
        if (arrayValue.length === 0) {
          // Empty array, assume string type
          fields.push(`repeated string ${fieldName} = ${fieldCounter++};`);
        } else {
          const firstItem = arrayValue[0];
          const firstItemType = getJsType(firstItem);
          
          if (firstItemType === 'object') {
            // Array of objects - create a message for the items
            const itemMessageName = generateMessageName(messageName, key.endsWith('s') ? key.slice(0, -1) : key + 'Item');
            const pathKey = [...path, key, '0'].join('.');
            
            // Check if we've seen this structure before
            if (processedObjects.has(JSON.stringify(firstItem))) {
              const existingType = processedObjects.get(JSON.stringify(firstItem))!;
              fields.push(`repeated ${existingType} ${fieldName} = ${fieldCounter++};`);
            } else {
              // Process the first item in the array to create a template
              const { messages: itemMessages, fields: itemFields } = processObject(
                firstItem, 
                itemMessageName, 
                depth + 1, 
                typeMappings,
                [...path, key, '0'],
                processedObjects
              );
              
              // Create the message for the array items
              const itemMessage = `message ${itemMessageName} {\n  ${itemFields.join('\n  ')}\n}`;
              messages.push(itemMessage);
              messages.push(...itemMessages);
              
              // Add a field for the array
              fields.push(`repeated ${itemMessageName} ${fieldName} = ${fieldCounter++};`);
              
              // Remember this structure for reuse
              processedObjects.set(JSON.stringify(firstItem), itemMessageName);
            }
          } else {
            // Array of primitive types
            const protoType = getProtoType(firstItemType, typeMappings);
            fields.push(`repeated ${protoType} ${fieldName} = ${fieldCounter++};`);
          }
        }
      } else {
        // Handle primitive types
        const protoType = getProtoType(jsType, typeMappings);
        fields.push(`${syntax === 'proto3' ? '' : 'optional '}${protoType} ${fieldName} = ${fieldCounter++};`);
      }
    }
    
    return { messages, fields };
  };

  // Convert JSON to Protocol Buffer
  const convertJsonToProto = () => {
    setError('');
    setIsLoading(true);
    setIsProtoGenerated(false);
    setProtoOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      let typeMappings: TypeMapping;
      
      // Parse the type mappings
      try {
        typeMappings = customTypes ? JSON.parse(typeMappingString) : {
          string: 'string',
          number: 'double',
          integer: 'int32',
          boolean: 'bool',
          array: 'repeated'
        };
      } catch (e) {
        setError(`Invalid type mapping format: ${e instanceof Error ? e.message : String(e)}`);
        setIsLoading(false);
        return;
      }
      
      // Clean the package name
      const cleanedPackageName = packageName.trim().replace(/[^a-zA-Z0-9_.]/g, '_');
      
      // Clean the main message name
      const cleanedMainMessageName = toPascalCase(cleanName(mainMessageName));
      
      // Process the JSON object
      const { messages, fields } = processObject(jsonData, cleanedMainMessageName, 0, typeMappings);
      
      // Create the Protocol Buffer definition
      const mainMessage = `message ${cleanedMainMessageName} {\n  ${fields.join('\n  ')}\n}`;
      
      let protoDefinition = '';
      
      if (outputFormat === 'single-file') {
        // Single file output
        protoDefinition = `syntax = "${syntax}";\n\n`;
        
        if (cleanedPackageName) {
          protoDefinition += `package ${cleanedPackageName};\n\n`;
        }
        
        protoDefinition += mainMessage + '\n\n' + messages.join('\n\n');
      } else {
        // Multiple files output (simulated as comments in a single output for this example)
        protoDefinition = `// This would typically be split across multiple files\n\n`;
        protoDefinition += `// File: ${cleanedMainMessageName.toLowerCase()}.proto\n`;
        protoDefinition += `syntax = "${syntax}";\n\n`;
        
        if (cleanedPackageName) {
          protoDefinition += `package ${cleanedPackageName};\n\n`;
        }
        
        // Import statements for referenced messages
        messages.forEach((_, index) => {
          protoDefinition += `import "${cleanedPackageName.toLowerCase()}/message${index + 1}.proto";\n`;
        });
        
        protoDefinition += `\n${mainMessage}\n\n`;
        
        // Separate files for each message
        messages.forEach((message, index) => {
          protoDefinition += `// File: ${cleanedPackageName.toLowerCase()}/message${index + 1}.proto\n`;
          protoDefinition += `syntax = "${syntax}";\n\n`;
          protoDefinition += `package ${cleanedPackageName};\n\n`;
          protoDefinition += `${message}\n\n`;
        });
      }
      
      setProtoOutput(protoDefinition);
      setIsProtoGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Protocol Buffer: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Proto to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(protoOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Proto file
  const downloadProto = () => {
    const blob = new Blob([protoOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${mainMessageName.toLowerCase()}.proto`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Protocol Buffers Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Protocol Buffers (.proto) format. Paste your JSON data below to generate a corresponding Protocol Buffer definition.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="packageName" className="block text-sm font-medium text-gray-700 mb-1">
                Package Name
              </label>
              <input
                type="text"
                id="packageName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={packageName}
                onChange={handlePackageNameChange}
                placeholder="Enter package name (e.g., my_package)"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="mainMessageName" className="block text-sm font-medium text-gray-700 mb-1">
                Main Message Name
              </label>
              <input
                type="text"
                id="mainMessageName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={mainMessageName}
                onChange={handleMainMessageNameChange}
                placeholder="Enter message name (e.g., Root)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="syntax" className="block text-sm font-medium text-gray-700 mb-1">
                    Protocol Buffer Syntax
                  </label>
                  <select
                    id="syntax"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={syntax}
                    onChange={(e) => setSyntax(e.target.value as ProtoSyntax)}
                  >
                    <option value="proto3">proto3 (Recommended)</option>
                    <option value="proto2">proto2 (Legacy)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="outputFormat" className="block text-sm font-medium text-gray-700 mb-1">
                    Output Format
                  </label>
                  <select
                    id="outputFormat"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                  >
                    <option value="single-file">Single File</option>
                    <option value="multiple-files">Multiple Files (Import Structure)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="nestedDepthLimit" className="block text-sm font-medium text-gray-700 mb-1">
                    Nested Depth Limit
                  </label>
                  <input
                    type="number"
                    id="nestedDepthLimit"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={nestedDepthLimit}
                    onChange={(e) => setNestedDepthLimit(parseInt(e.target.value))}
                    min="1"
                    max="20"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum depth for nested messages (1-20)
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="customTypes"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={customTypes}
                    onChange={(e) => setCustomTypes(e.target.checked)}
                  />
                  <label htmlFor="customTypes" className="ml-2 block text-sm text-gray-700">
                    Use Custom Type Mappings
                  </label>
                </div>
                
                {customTypes && (
                  <div>
                    <label htmlFor="typeMappingString" className="block text-sm font-medium text-gray-700 mb-1">
                      Type Mappings (JSON)
                    </label>
                    <textarea
                      id="typeMappingString"
                      className="w-full h-32 p-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                      value={typeMappingString}
                      onChange={(e) => setTypeMappingString(e.target.value)}
                      placeholder='{"string": "string", "number": "double", "integer": "int32", "boolean": "bool", "array": "repeated"}'
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Define custom mappings from JavaScript types to Protocol Buffer types
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToProto}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Protocol Buffer'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Protocol Buffer Output
            </label>
            
            {isProtoGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Proto'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadProto}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .proto
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isProtoGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Protocol Buffer output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isProtoGenerated && (
              <div className="flex-grow flex flex-col">
                <textarea
                  id="protoOutput"
                  className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={protoOutput}
                  readOnly
                />
              </div>
            )}
          </div>
          
          {isProtoGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Protocol Buffer Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated .proto file can be used with protoc compiler to generate code in various languages.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Protocol Buffer Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data structure and generates an equivalent Protocol Buffer (.proto) definition.
              It automatically creates message types, fields, and handles nested objects and arrays according to Protocol Buffer
              best practices.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Smart Type Inference</strong> - Automatically maps JavaScript types to Protocol Buffer types</li>
              <li><strong>Nested Object Support</strong> - Creates nested message types for complex JSON structures</li>
              <li><strong>Array Handling</strong> - Converts JSON arrays to repeated fields in Protocol Buffers</li>
              <li><strong>Proto2 and Proto3 Support</strong> - Generate definitions in either protocol version</li>
              <li><strong>Custom Type Mappings</strong> - Define your own mappings from JavaScript types to Protocol Buffer types</li>
              <li><strong>Field Naming Convention</strong> - Automatically converts camelCase to snake_case for field names</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Protocol Buffer Advantages</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Compact Data Format</strong> - Binary format that is 3-10x smaller than JSON</li>
              <li><strong>Faster Parsing</strong> - 20-100x faster than parsing JSON</li>
              <li><strong>Strongly Typed</strong> - Type safety across programming languages</li>
              <li><strong>Backward/Forward Compatibility</strong> - Add or remove fields without breaking existing code</li>
              <li><strong>Code Generation</strong> - Automatically generate code for multiple programming languages</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Next Steps</h3>
            <ol className="list-decimal pl-5 text-gray-600">
              <li>Download the generated .proto file</li>
              <li>Install the Protocol Buffer compiler (protoc) for your platform</li>
              <li>Run protoc to generate code in your target language:
                <pre className="bg-gray-100 p-2 mt-1 rounded-md text-sm">protoc --typescript_out=. your_file.proto</pre>
              </li>
              <li>Integrate the generated code in your application</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToProtobuf;